import React, { useEffect, useState } from 'react';
import {
  AppBar, Toolbar, IconButton, Typography, Box, Select, MenuItem, FormControl,
  Chip, Tooltip, Avatar, Menu, ListItemIcon, Divider, useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import { useLocation, useNavigate } from 'react-router-dom';
import { useColorMode } from '../context/ColorModeContext';
import { useDeviceSelection } from '../context/DeviceSelectionContext';
import { useAuth } from '../auth/AuthContext';
import { getSocket } from '../socket';
import api from '../services/api';

const TITLES = {
  '/': 'Dashboard',
  '/device': 'Device Control',
  '/history': 'Command History',
  '/analytics': 'Insights',
  '/admin': 'Admin Panel',
  '/preference': 'Preferences',
};

const TopBar = ({ onMenuClick }) => {
  const { mode, toggle } = useColorMode();
  const { selectedDeviceId, setSelectedDeviceId } = useDeviceSelection();
  const { role, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((t) => t.breakpoints.down('md'));

  const [devices, setDevices] = useState([]);
  const [onlineIds, setOnlineIds] = useState([]);
  const [connected, setConnected] = useState(false);
  const [anchor, setAnchor] = useState(null);

  const title = TITLES[location.pathname] ||
    (location.pathname.startsWith('/device') ? 'Device Control' : 'AQUATRON');

  useEffect(() => {
    let mounted = true;
    api.get('/device/devices/registered')
      .then((r) => { if (mounted) setDevices(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    const socket = getSocket();
    setConnected(socket.connected);
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    const onStatus = (d) => {
      if (d && d.deviceId) {
        setOnlineIds((prev) => {
          const set = new Set(prev);
          if (d.online) set.add(d.deviceId); else set.delete(d.deviceId);
          return Array.from(set);
        });
      }
    };
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('device-status', onStatus);
    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('device-status', onStatus);
      mounted = false;
    };
  }, []);

  const sorted = [...devices].sort((a, b) => {
    const ao = onlineIds.includes(a.deviceId), bo = onlineIds.includes(b.deviceId);
    if (ao === bo) return (a.customName || a.deviceId).localeCompare(b.customName || b.deviceId);
    return ao ? -1 : 1;
  });

  return (
    <AppBar position="sticky">
      <Toolbar sx={{ gap: 1.5, minHeight: { xs: 60, md: 64 } }}>
        {isMobile && (
          <IconButton edge="start" onClick={onMenuClick} aria-label="menu"><MenuIcon /></IconButton>
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, mr: 1, whiteSpace: 'nowrap' }}>{title}</Typography>

        <Box sx={{ flex: 1 }} />

        {/* Global device selector */}
        <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 210 } }}>
          <Select
            displayEmpty
            value={selectedDeviceId || ''}
            onChange={(e) => setSelectedDeviceId(e.target.value)}
            renderValue={(val) => {
              if (!val) return <Typography variant="body2" color="text.secondary">Select device</Typography>;
              const d = devices.find((x) => x.deviceId === val);
              const online = onlineIds.includes(val);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircleIcon sx={{ fontSize: 9, color: online ? 'success.main' : 'text.disabled' }} />
                  <Typography variant="body2" noWrap>{d?.customName || val}</Typography>
                </Box>
              );
            }}
          >
            {sorted.length === 0 && <MenuItem value="" disabled>No registered devices</MenuItem>}
            {sorted.map((d) => (
              <MenuItem key={d.deviceId} value={d.deviceId}>
                <CircleIcon sx={{ fontSize: 9, mr: 1, color: onlineIds.includes(d.deviceId) ? 'success.main' : 'text.disabled' }} />
                {d.customName || d.deviceId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Realtime connection */}
        <Tooltip title={connected ? 'Realtime connected' : 'Realtime disconnected'}>
          <Chip
            size="small"
            variant="outlined"
            color={connected ? 'success' : 'default'}
            icon={<CircleIcon sx={{ fontSize: 10 }} />}
            label={connected ? 'Live' : 'Offline'}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          />
        </Tooltip>

        <Tooltip title={mode === 'dark' ? 'Switch to light' : 'Switch to dark'}>
          <IconButton onClick={toggle} aria-label="toggle theme">
            {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title={role || 'account'}>
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>
              {(role || 'U').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">Signed in</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{role || 'user'}</Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setAnchor(null); navigate('/preference'); }}>
            <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>Preferences
          </MenuItem>
          <MenuItem onClick={() => { setAnchor(null); logout(); navigate('/login'); }}>
            <ListItemIcon><LogoutOutlinedIcon fontSize="small" /></ListItemIcon>Log out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
